import { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useUser } from '@clerk/react';
import {
  Team,
  TeamInviteOutcome,
  TeamMember,
  TeamRole,
} from '@this+that/models';
import log from 'loglevel';

import {
  InviteMemberFormType,
  TeamInfo,
  TeamMembersContext,
  TeamMembersState,
} from './TeamMembersContext';
import { useCustomToast } from '../../hooks/useCustomToast';
import {
  useGetPaginatedTeamMembers,
  useGetTeamById,
  useRemoveMember,
  useUpdateMember,
} from '../../services/team.service';
import {
  useCreateInvite,
  useDeleteInvite,
} from '../../services/teamInvite.service';
import { useActiveTeamId, useTeamStore } from '../../store/Team/useTeamStore';

const DEFAULT_TEAM_MEMBERS_PAGE_SIZE = 100;

function toTeamInfo(team: Team): TeamInfo {
  return {
    name: team.name,
    userRole: team.userRole,
    userFeatures: team.userFeatures,
  } as TeamInfo;
}

function toTeamMembers(members: Team[]): TeamMember[] {
  return members.map((t) => ({
    id: t.userId!,
    name: t.userName,
    email: t.userEmail,
    title: t.userTitle,
  }));
}

const EMPTY_TEAM_INFO = {} as TeamInfo;

export function TeamMembersProvider({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  const { promiseToast, successMessage, errorMessage, warningMessage } =
    useCustomToast();
  const { getTeamById } = useGetTeamById();
  const { getPaginatedTeamMembers } = useGetPaginatedTeamMembers();
  const { deleteInvite } = useDeleteInvite();
  const { createInvite, loading: isInviteLoading } = useCreateInvite();
  const { removeMember, loading: removeLoading } = useRemoveMember();
  const { updateMember } = useUpdateMember();

  const { isLoaded: isClerkLoaded, isSignedIn } = useUser();
  const isAuthReady = isClerkLoaded && isSignedIn;

  const activeTeamId = useActiveTeamId();
  const activeTeamCache = useTeamStore((s) => s.teamsById[activeTeamId ?? '']);
  const storeUserTeamIds = useTeamStore((s) => s.userTeamIds);

  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!isAuthReady || !activeTeamId || activeTeamCache) {
      setIsLoading(false);
      return;
    }
    let cancelled = false;
    setIsLoading(true);
    (async () => {
      try {
        const [team, membersPage] = await Promise.all([
          getTeamById(activeTeamId),
          getPaginatedTeamMembers(activeTeamId, DEFAULT_TEAM_MEMBERS_PAGE_SIZE),
        ]);
        if (cancelled || !team) {
          return;
        }
        useTeamStore
          .getState()
          .setTeamData(activeTeamId, team, membersPage?.members ?? []);
      } catch (error) {
        log.error('Error fetching team data:', error);
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthReady, activeTeamId, activeTeamCache]);

  const state = useMemo<TeamMembersState>(
    () => ({
      teamMembers: toTeamMembers(activeTeamCache?.members ?? []),
      teamInfo: activeTeamCache
        ? toTeamInfo(activeTeamCache.team)
        : EMPTY_TEAM_INFO,
      userTeamIds: storeUserTeamIds ?? [],
      isLoading,
    }),
    [activeTeamCache, storeUserTeamIds, isLoading],
  );

  const handleRemoveMember = useCallback(
    async (teamId: string, id?: string) => {
      if (!id) {
        return;
      }
      try {
        const removeMemberPromise = removeMember(teamId, id);
        promiseToast(
          removeMemberPromise,
          t('team.loadingMessage'),
          t('team.successMessages.deleteMember'),
          t('team.errorMessage.somethingWentWrong'),
        );
        await removeMemberPromise;
        useTeamStore.getState().removeMemberFromTeam(teamId, id);
      } catch (error) {
        log.error('Error while removing a team member', error);
      }
    },
    [t, removeMember, promiseToast],
  );

  const handleAddMember = useCallback(
    async (data: InviteMemberFormType) => {
      if (!activeTeamId) {
        return null;
      }
      try {
        const result = await createInvite(
          activeTeamId,
          data.inviteeEmail,
          data.inviteeRole as TeamRole,
        );

        if (result.__typename === 'TeamInviteFailed') {
          errorMessage(t(`teamHub.toasts.inviteFailure.${result.reason}`));
          return null;
        }

        if (result.outcome === TeamInviteOutcome.SENT) {
          successMessage(t('team.successMessages.inviteSent'));
        } else {
          warningMessage(
            t('teamHub.toasts.inviteAlreadyPending', {
              email: data.inviteeEmail,
            }),
          );
        }

        return result.invite;
      } catch (error) {
        log.error('Error while creating a team invite', error);
        errorMessage(t('team.errorMessage.somethingWentWrong'));
      }
      return null;
    },
    [
      activeTeamId,
      createInvite,
      t,
      successMessage,
      errorMessage,
      warningMessage,
    ],
  );

  const handleDeleteInvite = useCallback(
    async (teamId: string, email?: string) => {
      if (!email) {
        return;
      }
      try {
        const deleteInvitePromise = deleteInvite(teamId, email);
        promiseToast(
          deleteInvitePromise,
          t('team.loadingMessage'),
          t('team.successMessages.deleteMember'),
          t('team.errorMessage.somethingWentWrong'),
        );
        await deleteInvitePromise;
      } catch (error) {
        log.error('Error while removing a team member', error);
      }
    },
    [t, deleteInvite, promiseToast],
  );

  const handleUpdateTeamMember = useCallback(
    async (teamInstance: Team) => {
      if (!teamInstance.id || !teamInstance.userId) {
        log.error('Cannot update a team member without a team and user id');
        return null;
      }

      try {
        const success = await updateMember({
          id: teamInstance.id,
          userId: teamInstance.userId,
          userRole: teamInstance.userRole ?? TeamRole.MEMBER,
        });
        if (success) {
          successMessage(t('team.successMessages.updateMember'));
          return success;
        }
      } catch (error) {
        log.error('Error while updating a team member', error);
        errorMessage(t('team.errorMessage.somethingWentWrong'));
      }
      return null;
    },
    [t, updateMember, successMessage, errorMessage],
  );

  return (
    <TeamMembersContext.Provider
      value={{
        state,
        isLoading,
        removeLoading,
        handleAddMember,
        isInviteLoading,
        handleDeleteInvite,
        handleRemoveMember,
        handleUpdateTeamMember,
      }}
    >
      {children}
    </TeamMembersContext.Provider>
  );
}
